import { useI18n } from '@muse/i18n'

import { type AnalyticsPlacementTypes, sendEvent, setAnalyticsTrigger } from '@core/analytics'
import { useHydratedValue } from '@muse-web/router'
import { Logger } from '@core/logger'
import { queryClient } from '@core/query'
import type { TIconButtonVariant } from '@ds/components'
import { useAuthModal } from '@features/auth'
import { useCurrentUser } from '@features/session'

import { likedAudioIdsQuery } from '../helpers/likedAudioIdsQuery'
import { useLikeAudio } from '../hooks/useLikeAudio'
import { useLikedAudioIds } from '../hooks/useLikedAudioIds'
import { useUnlikeAudio } from '../hooks/useUnlikeAudio'
import { LikeButton } from '../components/like-button'
import { useAudioLikesCount } from '../hooks/useAudioLikesCount'
import { useFirstLikeDialog } from '../hooks/useFirstLikeDialog'

export type TLikeButtonContainerProps = {
    audioId: string
    placement: AnalyticsPlacementTypes
    variant?: TIconButtonVariant
    as?: 'icon' | 'button' | 'plain'
    /** Server-side likes count; the button shows it and follows my own like/unlike. */
    likesCount?: number
    showCount?: boolean
}

export const LikeButtonContainer = ({
    audioId,
    placement,
    variant,
    as,
    likesCount = 0,
    showCount = true,
}: TLikeButtonContainerProps) => {
    const { t } = useI18n()
    const { open: openAuthModal } = useAuthModal()
    const { id: myId } = useCurrentUser()
    const isAuthenticated = myId !== 0

    const likedAudioIds = useLikedAudioIds(isAuthenticated)
    const like = useLikeAudio()
    const unlike = useUnlikeAudio()
    const firstLikeDialog = useFirstLikeDialog()
    const count = useHydratedValue(useAudioLikesCount(audioId, likesCount), likesCount)

    const isLiked = useHydratedValue(likedAudioIds.data?.includes(audioId) ?? false, false)

    const placementPayload: {
        audio: string
        params__key: ['player_placement']
        params__str_value: [AnalyticsPlacementTypes]
    } = {
        audio: audioId,
        params__key: ['player_placement'],
        params__str_value: [placement],
    }

    const handleClick = async () => {
        if (isLiked) {
            sendEvent({ name: 'unlike_button_click', payload: placementPayload })
        } else {
            sendEvent({ name: 'like_button_click', payload: placementPayload })
        }

        if (!isAuthenticated) {
            setAnalyticsTrigger('like_click')
            openAuthModal({
                intent: { kind: 'like', audioId, placement },
            })

            return
        }

        let currentLikedAudioIds = likedAudioIds.data

        if (!currentLikedAudioIds) {
            try {
                currentLikedAudioIds = await queryClient.fetchQuery(likedAudioIdsQuery())
            } catch (error) {
                Logger.error('Liked audio ids fetch error', { error })

                return
            }
        }

        if (currentLikedAudioIds.includes(audioId)) {
            unlike.mutate(audioId, {
                onSuccess: () => sendEvent({ name: 'unlike_click', payload: placementPayload }),
            })

            return
        }

        like.mutate(audioId, {
            onSuccess: () => sendEvent({ name: 'like_click', payload: placementPayload }),
        })

        if (!firstLikeDialog.hasSeen) {
            firstLikeDialog.open()
        }
    }

    return (
        <LikeButton
            count={showCount ? count : 0}
            isLiked={isLiked}
            label={t(isLiked ? 'i18n.likeButtonLiked' : 'i18n.likeButtonLike')}
            variant={variant}
            as={as}
            onClick={handleClick}
        />
    )
}
